import React, { useEffect, useMemo, useState } from 'react';

import styles from './PreconfigPatientButton.module.css';

import { getEnvironmentApiUrl } from '../../../services/General';

import { useTranslation } from 'react-i18next';
import useHttp from '../../../hooks/useHttp';

import Modal from '../../UI/Modal/Modal';
import PatientAssignPreconfigsDialog from '../PatientAssignPreconfigsDialog/PatientAssignPreconfigsDialog';
import PatientPreconfigDialog from '../PatientPreconfigDialog/PatientPreconfigDialog';
import CancelConfirmButtons from '../../UI/CancelConfirmButtons/CancelConfirmButtons';

import { Patient } from '../../../interfaces/Patient';
import { PatientPreconfig, Preconfiguration } from '../../../interfaces/Preconfigurations';

interface Props {
    patient: Patient;
    refreshPatientSilently: () => Promise<void>;
}

const PreconfigPatientButton: React.FC<Props> = ({ patient, refreshPatientSilently }) => {
    const { t } = useTranslation();

    const [showDialog, setShowModal] = useState(false);
    const [optimisticPatientPreconfig, setOptimisticPatientPreconfig] = useState<PatientPreconfig | null>(null);
    const [fetchedPreconfig, setFetchedPreconfig] = useState<Preconfiguration | null>(null);
    const [fetchedPreconfigId, setFetchedPreconfigId] = useState<number | string | null>(null);

    const { sendRequest: getPreconfigById } = useHttp<Preconfiguration>();

    const currentPatientPreconfig = useMemo(() => {
        return (
            patient.patient_preconfigs?.find(pp => pp.end_date == null) ??
            patient.patient_preconfigs?.[0] ??
            null
        );
    }, [patient.patient_preconfigs]);

    const effectivePatientPreconfig = useMemo(() => {
        return currentPatientPreconfig ?? optimisticPatientPreconfig ?? null;
    }, [currentPatientPreconfig, optimisticPatientPreconfig]);

    const patientPreconfigID = effectivePatientPreconfig?.id;
    const currentCaseID = patient.cases?.[0]?.id;

    const preconfigIdToRecover = useMemo(() => {
        return effectivePatientPreconfig?.preconfig_id ?? null;
    }, [effectivePatientPreconfig]);

    useEffect(() => {
        if (!preconfigIdToRecover || effectivePatientPreconfig?.preconfig || fetchedPreconfigId === preconfigIdToRecover) return;

        getPreconfigById(
            {
                url: getEnvironmentApiUrl() + 'preconfig/' + preconfigIdToRecover,
                method: 'GET',
            },
            (data) => {
                setFetchedPreconfig(data);
                setFetchedPreconfigId(preconfigIdToRecover);
            },
            () => {
                setFetchedPreconfig(null);
                setFetchedPreconfigId(preconfigIdToRecover);
            }
        );
    }, [effectivePatientPreconfig?.preconfig, fetchedPreconfigId, getPreconfigById, preconfigIdToRecover]);

    const resolvedPreconfig = useMemo(() => {
        if (effectivePatientPreconfig?.preconfig) return effectivePatientPreconfig.preconfig;
        if (fetchedPreconfigId === preconfigIdToRecover) return fetchedPreconfig;
        return null;
    }, [effectivePatientPreconfig?.preconfig, fetchedPreconfig, fetchedPreconfigId, preconfigIdToRecover]);

    const hasAssignedPreconfig = Boolean(effectivePatientPreconfig);
    const canOpenEditor = patientPreconfigID != null && resolvedPreconfig != null;
    const waitingRealAssignment = patientPreconfigID == null && resolvedPreconfig != null;

    const hasOverride = Boolean(
        effectivePatientPreconfig?.override_preconfig_id != null ||
        effectivePatientPreconfig?.override_preconfig
    );

    return (
        <>
            {hasAssignedPreconfig ? (
                <button onClick={() => setShowModal(true)} className={styles.preconfigButton}>
                    <span className={styles.preconfigName}>{resolvedPreconfig?.name ?? ''}</span>
                    {hasOverride && <span className={styles.red}>*</span>}
                </button>
            ) : (
                <button onClick={() => setShowModal(true)} className={styles.assignButton}>
                    {t('assign')}
                </button>
            )}

            <Modal show={showDialog} setShowModal={setShowModal}>
                {canOpenEditor && resolvedPreconfig ? (
                    <PatientPreconfigDialog
                        patient={patient}
                        patientPreconfigID={patientPreconfigID}
                        defaultPreconfig={resolvedPreconfig}
                        setShow={setShowModal}
                        refreshPatientSilently={refreshPatientSilently}
                    />
                ) : waitingRealAssignment && resolvedPreconfig ? (
                    <div className='dialog'>
                        <h1 className='dialogTitle'>{resolvedPreconfig.name}</h1>
                        <div style={{ padding: '1rem 0', textAlign: 'center' }}>
                            {t('loading')}...
                        </div>
                        <CancelConfirmButtons
                            onCancel={() => setShowModal(false)}
                            onConfirm={() => setShowModal(false)}
                        />
                    </div>
                ) : currentCaseID ? (
                    <PatientAssignPreconfigsDialog
                        caseID={currentCaseID}
                        setShow={setShowModal}
                        onAssigned={setOptimisticPatientPreconfig}
                        refreshPatientSilently={refreshPatientSilently}
                    />
                ) : null}
            </Modal>
        </>
    );
};

export default PreconfigPatientButton;